import React, { useEffect, useRef, useState } from 'react';
import { ValidationResult } from '../lib/realtimeValidation';
import { ValidationFeedback } from './ValidationFeedback';
import { API_BASE_URL } from '../constants';

// Predictive sponsor/referral username picker. Drop-in replacement for
// ValidationInput on referral fields: identical props + markup so the existing
// onChange/validation wiring is untouched, plus a hardened suggestion dropdown
// served by /api/search-referral (prefix match, rate limited, username only).
interface ReferralAutocompleteProps {
  id: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  validation: ValidationResult | null;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  label?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
}

interface Suggestion {
  username: string;
  name: string;
}

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

export const ReferralAutocomplete: React.FC<ReferralAutocompleteProps> = ({
  id, name, type = 'text', value, onChange, validation,
  placeholder, autoComplete = 'off', className = '', label, required = false, maxLength, disabled = false,
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressRef = useRef(false); // skip the search fired by a programmatic select

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (suppressRef.current) { suppressRef.current = false; return; }
    if (disabled) { setSuggestions([]); setOpen(false); return; }
    const q = (value || '').trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < MIN_CHARS) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/search-referral`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q }),
        });
        const data = await res.json();
        const list: Suggestion[] = res.ok && Array.isArray(data.results) ? data.results : [];
        setSuggestions(list);
        setHighlight(-1);
        setOpen(list.length > 0);
      } catch {
        setSuggestions([]); setOpen(false);
      }
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, disabled]);

  const selectSuggestion = (s: Suggestion) => {
    suppressRef.current = true;
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
    onChange({ target: { name, value: s.username } } as React.ChangeEvent<HTMLInputElement>);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < suggestions.length) { e.preventDefault(); selectSuggestion(suggestions[highlight]); }
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  const validationClass = validation
    ? (validation.type === 'success' ? 'validation-success'
      : validation.type === 'error' ? 'validation-error'
      : validation.type === 'checking' ? 'validation-checking' : '')
    : '';

  return (
    <div className="validation-input-container" ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label} {required && <span style={{ color: 'var(--error)' }}>*</span>}
        </label>
      )}
      <div className="validation-input-wrapper">
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          autoComplete={autoComplete}
          disabled={disabled}
          className={`form-input validation-input ${validationClass} ${className}`}
          placeholder={placeholder}
          maxLength={maxLength}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {!validation?.isChecking && validation && (
          <div className={`validation-icon ${validation.isValid ? 'validation-icon-success' : 'validation-icon-error'}`}>
            <svg className="validation-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d={validation.isValid ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'} />
            </svg>
          </div>
        )}
        {validation?.isChecking && (
          <div className="validation-icon validation-icon-checking"><div className="loading-spinner"></div></div>
        )}
        {open && suggestions.length > 0 && (
          <ul className="referral-autocomplete-list" role="listbox" style={dropdownStyle}>
            {suggestions.map((s, i) => (
              <li
                key={s.username}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                onMouseEnter={() => setHighlight(i)}
                style={{ ...itemStyle, background: i === highlight ? 'rgba(212,175,55,0.18)' : 'transparent' }}
              >
                <span style={{ fontWeight: 600, color: '#d4af37' }}>{s.username}</span>
                {s.name && <span style={{ marginLeft: 8, color: '#9ca3af', fontSize: 13 }}>{s.name}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <ValidationFeedback validation={validation} />
    </div>
  );
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, margin: '4px 0 0',
  padding: 0, listStyle: 'none', maxHeight: 240, overflowY: 'auto',
  background: '#1f2937', border: '1px solid #4b5563', borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
};

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '10px 12px', cursor: 'pointer',
  borderBottom: '1px solid rgba(75,85,99,0.4)',
};
